import { useEffect, useMemo, useState } from "react";

import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash.debounce";

import { Slider } from "@components/Slider";
import { Typography } from "@components/Typography";

import { EToySessionState } from "@api/toys";
import type {
	IUpdateMotionRequest,
	TActuator,
	TActuatorCommand,
	TActuatorCommands,
	TActuatorParameter,
	TGetToysSessionResponse,
	TPreset,
} from "@api/toys";

import { useToysStore } from "@storage/global/useToysStore";
import { useUpdateToysMotionMutation } from "@storage/queries/toys";

import { cn } from "@lib/utils";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";
import { DELAY_TIME_300 } from "@configs/constants";
import { actuatorIDMap, toyPresetDescriptionMap } from "@configs/toys";

type TMotionDraft = Pick<IUpdateMotionRequest, "motion">;

interface IToyActuatorControlsProps {
	actuator: TActuator;
	commands?: TActuatorCommands;
	disabled?: boolean;
	presets: TPreset[];
	sessionToken: string;
	layout?: "desktop" | "compact" | "inline";
}

export const ToyActuatorControls = ({
	actuator,
	commands,
	disabled = false,
	presets,
	sessionToken,
	layout = "desktop",
}: IToyActuatorControlsProps) => {
	const [draft, setDraft] = useState<TMotionDraft | null>(null);
	const queryClient = useQueryClient();

	const { mutate: updateMotion, isPending } = useUpdateToysMotionMutation(sessionToken);
	const { activePrivateShowId } = useToysStore();
	const pendingPlayCount = useIsMutating({
		mutationKey: [QUERY_KEYS.TOYS_SESSION, activePrivateShowId, API_PREFIXES.PLAY_TOYS],
		exact: true,
	});
	const pendingStopCount = useIsMutating({
		mutationKey: [QUERY_KEYS.TOYS_SESSION, activePrivateShowId, API_PREFIXES.STOP_TOYS],
		exact: true,
	});
	const isPlaybackPending = pendingPlayCount > 0 || pendingStopCount > 0;

	const actuatorId = actuatorIDMap[actuator.id] ?? actuator.id;
	const parameters = Object.entries(actuator.params).filter(([, parameter]) => parameter.max > parameter.min);
	const values = Object.fromEntries(
		Object.entries(actuator.params).map(([name, parameter]) => [
			name,
			draft?.motion.actuators[actuatorId]?.[name] ??
				commands?.[actuatorId]?.[name] ??
				parameter.default ??
				(parameter.kind === "span" ? [parameter.min, parameter.max] : parameter.min),
		]),
	) as TActuatorCommand;
	const availablePresets = presets.filter((preset) => preset.actuator_type === actuator.type);
	const areControlsDisabled = disabled || !sessionToken || isPending || isPlaybackPending;
	const isCompact = layout !== "desktop";

	const debouncedUpdate = useMemo(
		() =>
			debounce((nextMotion: TMotionDraft, presetId?: string) => {
				const sessionKey = [QUERY_KEYS.TOYS_SESSION, activePrivateShowId];
				if (
					!sessionToken ||
					disabled ||
					isPlaybackPending ||
					useToysStore.getState().activePrivateShowId !== activePrivateShowId ||
					queryClient.isMutating({ mutationKey: sessionKey }) > 0 ||
					queryClient.getQueryData<TGetToysSessionResponse>(sessionKey)?.state !== EToySessionState.GRANTED_RUNNING
				) {
					setDraft(null);
					return;
				}

				const request = presetId === undefined ? nextMotion : { ...nextMotion, preset_id: presetId };
				updateMotion(request, {
					onSettled: () => setDraft((current) => (current === nextMotion ? null : current)),
				});
			}, DELAY_TIME_300),
		[activePrivateShowId, actuatorId, disabled, isPlaybackPending, queryClient, sessionToken, updateMotion],
	);

	useEffect(() => {
		if (isPlaybackPending) {
			setDraft(null);
		}

		return () => debouncedUpdate.cancel(); // Cancel queued slider/preset updates when Play or Stop starts. Only one request allowed at a time.
	}, [debouncedUpdate, isPlaybackPending]);

	const handleValueChange = (
		name: string,
		parameter: TActuatorParameter,
		value: number | readonly number[],
		committed = false,
	) => {
		if (areControlsDisabled) {
			return;
		}

		debouncedUpdate.cancel();
		const sliderValues = typeof value === "number" ? [value] : value;
		const nextCommand: TActuatorCommand = {
			...values,
			[name]: parameter.kind === "span" ? [sliderValues[0], sliderValues[1]] : sliderValues[0],
		};
		const nextMotion: TMotionDraft = { motion: { actuators: { [actuatorId]: nextCommand } } };
		setDraft(nextMotion);

		if (committed) {
			debouncedUpdate(nextMotion);
		}
	};

	const handleApplyPreset = (preset: TPreset) => {
		if (areControlsDisabled) {
			return;
		}

		const nextCommand = Object.fromEntries(
			Object.keys(actuator.params).map((name) => [name, preset.command[name] ?? values[name]]),
		) as TActuatorCommand;
		const nextMotion: TMotionDraft = {
			motion: { actuators: { [actuatorId]: nextCommand } },
		};
		setDraft(nextMotion);
		debouncedUpdate(nextMotion, preset.id);
	};

	return (
		<div
			aria-disabled={areControlsDisabled}
			className={cn(
				"flex w-full min-w-0 flex-col gap-5.25",
				layout === "compact" && "gap-3.5 tablet:grid tablet:min-h-25 tablet:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]",
				layout === "inline" && "gap-0",
			)}
			onPointerDownCapture={() => debouncedUpdate.cancel()}
		>
			<div
				className={cn(
					"flex min-w-0 flex-col gap-5.25",
					isCompact && "justify-center gap-3.75",
					layout === "inline" && "flex-row gap-3.75",
				)}
			>
				{parameters.map(([name, parameter]) => {
					const value = values[name];
					const label = name.replace(/_/g, " ");
					const isSpan = parameter.kind === "span";

					return (
						<div key={name} className={cn("flex w-full min-w-0 flex-col items-center gap-2", isCompact && "gap-0.5")}>
							<div className={cn("grid h-4 w-full grid-cols-[1fr_auto_1fr] items-center", isCompact && "h-3")}>
								<Typography
									type="span"
									size="xs"
									color="grey-tertiary"
									className={cn("leading-4", isCompact && "text-[10px] leading-3", layout === "inline" && "text-[8px]")}
								>
									{isSpan ? "Down" : "Min"}
								</Typography>
								<Typography
									type="span"
									size="sm"
									weight="bold"
									align="center"
									className={cn("capitalize", isCompact && "text-[10px] leading-3")}
								>
									{label}
								</Typography>
								<Typography
									type="span"
									size="xs"
									color="grey-tertiary"
									align="right"
									className={cn("leading-4", isCompact && "text-[10px] leading-3", layout === "inline" && "text-[8px]")}
								>
									{isSpan ? "Up" : "Max"}
								</Typography>
							</div>
							<div
								className={cn(
									"flex h-7 w-full items-center rounded-[30px] bg-secondary px-3 shadow-[0_2px_8px_rgba(0,0,0,0.4)]",
									isCompact && "h-4.5 px-2.5",
								)}
							>
								<Slider
									aria-label={`${actuator.id} ${label} control`}
									value={typeof value === "number" ? [value] : value}
									min={parameter.min}
									max={parameter.max}
									step={parameter.integral ? 1 : (parameter.max - parameter.min) / 100}
									disabled={areControlsDisabled}
									onValueChange={(nextValue) => handleValueChange(name, parameter, nextValue)}
									onValueCommitted={(nextValue) => handleValueChange(name, parameter, nextValue, true)}
									className={cn(
										`w-full **:data-[slot=slider-thumb]:size-3.5 **:data-[slot=slider-thumb]:border-2
										**:data-[slot=slider-track]:bg-block`,
										disabled && "**:data-disabled:opacity-100",
										isSpan ? "**:data-[slot=slider-range]:bg-primary" : "**:data-[slot=slider-range]:bg-primary-pink",
									)}
								/>
							</div>
						</div>
					);
				})}
			</div>

			{layout !== "inline" && availablePresets.length > 0 ? (
				<div className={cn("flex w-full min-w-0 flex-col items-center gap-2", isCompact && "justify-center gap-0.5")}>
					<Typography type="h2" size="sm" weight="bold" className={cn(isCompact && "text-[10px] leading-3")}>
						Presets
					</Typography>
					<div
						className={cn(
							"grid w-full grid-cols-2 rounded-lg border border-secondary p-1",
							isCompact && "gap-0.75 p-0.75",
						)}
					>
						{availablePresets.map((preset) => (
							<button
								key={preset.id}
								type="button"
								aria-label={`Use ${preset.title} preset`}
								disabled={areControlsDisabled}
								onClick={() => handleApplyPreset(preset)}
								className={cn(
									`flex min-h-12.75 cursor-pointer flex-col items-center justify-center gap-1 rounded-sm px-2 py-1.75
										transition-colors outline-none hover:bg-grey-hover focus-visible:ring-2 focus-visible:ring-primary
										active:bg-secondary disabled:pointer-events-none disabled:opacity-50`,
									disabled && "disabled:opacity-100",
									isCompact && "min-h-7 gap-0 px-1 py-0.5",
								)}
							>
								<Typography
									type="span"
									size="base"
									align="center"
									className={cn("leading-4.75", isCompact && "text-[10px] leading-3")}
								>
									{preset.title}
								</Typography>
								{isCompact && toyPresetDescriptionMap[preset.title.toLowerCase()] ? (
									<Typography type="span" color="grey-tertiary" align="center" className="text-[8px] leading-2.5">
										{toyPresetDescriptionMap[preset.title.toLowerCase()]}
									</Typography>
								) : null}
							</button>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
};
